import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, MapPin, Mail, BookOpen, Video } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeachersPage = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get(`${API_URL}/teachers`);
        setTeachers(response.data);
      } catch (error) {
        console.error('Error fetching teachers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeachers();
  }, []);

  const handleJoinCall = (teacher) => {
    if (!user) {
      toast.error('Please login to join a video call');
      navigate('/login');
      return;
    }
    // Generate room name based on teacher name
    const roomName = `KhalsaSidak-Teacher-${teacher.name.replace(/\s+/g, '-')}`;
    navigate(`/video-call/${roomName}`);
  };

  const filteredTeachers = teachers.filter(teacher =>
    teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.expertise?.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="teachers-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Sikh Teachers & Scholars
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Connect with experienced teachers for Gurbani, Kirtan, Sikh philosophy, and Gurmukhi learning.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or expertise..."
              className="pl-12 py-6 text-lg rounded-full border-saffron-200 focus:border-saffron-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="teacher-search"
            />
          </div>
        </div>

        {/* Teachers Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                  <div className="flex gap-2 justify-center">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredTeachers.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600">No teachers found</h3>
            <p className="text-gray-500">Try adjusting your search terms</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTeachers.map((teacher, index) => (
              <Card 
                key={teacher.id} 
                className="card-hover bg-white border-gray-100"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`teacher-card-${teacher.id}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <img
                      src={teacher.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(teacher.name)}&background=FF9933&color=fff&size=96`}
                      alt={teacher.name}
                      className="w-full h-full rounded-full object-cover border-4 border-saffron-100"
                    />
                  </div>
                  
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                    {teacher.name}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {teacher.bio}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    {teacher.expertise?.slice(0, 3).map((skill, i) => (
                      <Badge key={i} variant="secondary" className="bg-saffron-50 text-saffron-700 border-saffron-200">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                    {teacher.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {teacher.location}
                      </span>
                    )}
                  </div>
                  
                  {teacher.contact_email && (
                    <a
                      href={`mailto:${teacher.contact_email}`}
                      className="mt-4 inline-flex items-center gap-2 text-royal-600 hover:text-royal-700 font-medium text-sm"
                    >
                      <Mail className="w-4 h-4" />
                      Contact Teacher
                    </a>
                  )}
                  
                  {/* Video Call Button */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Button
                      onClick={() => handleJoinCall(teacher)}
                      className="w-full bg-saffron-500 hover:bg-saffron-600 text-white"
                      data-testid={`join-call-${teacher.id}`}
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Join Video Call
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeachersPage;
